import React, { useState, useEffect, useMemo } from 'react';
import AutoTranslateText from '../components/AutoTranslateText';
import PayPalOrdersPanel from '../components/PayPalOrdersPanel';
import StripeOrdersPanel from '../components/StripeOrdersPanel';
import { PluginInterface, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { useFileManager } from '../hooks/useFileManager';
import { usePaymentApiKeys } from '../hooks/usePaymentApiKeys';
import { getRtcToken } from '../services/livekit';
import { fetchAPI } from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayPalCheckoutButtons from '../components/PayPalCheckoutButtons';
import { Editor } from '@tinymce/tinymce-react';
import { withTabLink } from '../utils/tinymceTabLink';
import { useTinyMceApiKey } from '../hooks/useTinyMceApiKey';

// --- TYPES ---

export interface Registrant {
  id: string;
  name: string;
  email: string;
  registeredAt: string;
  status: 'confirmed' | 'pending';
}

export interface LiveClassTariff {
  id: string;
  name: string;
  price: number;
  description?: string;
}

export interface LiveClassCoupon {
  code: string;
  type: 'percent' | 'fixed';
  amount: number;
  active: boolean;
}

export interface LiveClassAlertSettings {
  enabled: boolean;
  channels: {
    notification: boolean;
    sms: boolean;
    whatsapp: boolean;
  };
  advanceValue: number;
  advanceUnit: 'minutes' | 'hours' | 'days';
  message: string;
  sendAtStart: boolean;
  startMessage: string;
}

export interface LiveClassConfig {
  id?: string; // ✅ AJOUT : ID unique pour le système de plugin
  ownerId?: string | number;
  ownerEmail?: string;
  eventId: string; // ID pour la room LiveKit
  title: string;
  description: string;
  termsCgv?: string;
  termsRgpd?: string;
  currency: string;
  scheduledAt: string;
  duration: number;
  allowLateRegistrationMinutes?: number;
  instructorName: string;
  coverImage?: string;
  coverMediaType?: 'image' | 'video';
  registrants: Registrant[];
  tariffs: LiveClassTariff[];
  coupons?: LiveClassCoupon[];
  alertSettings?: LiveClassAlertSettings;
  paymentMethods: {
    stripe: boolean;
    paypal: boolean;
  };
}

interface MerchantKeys {
  stripePublicKey?: string | null;
  paypalClientId?: string | null;
}

// --- HELPER : Récupération sécurisée de l'utilisateur ---
const useSafeUser = () => {
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (e) {
      console.warn("Impossible de récupérer l'utilisateur", e);
    }
  }, []);
  return user;
};

const resolveOwnerId = (config: LiveClassConfig): string | number | undefined => {
  return (
    config.ownerId ||
    (config as any).userId ||
    (config as any).createdBy ||
    (config as any).created_by ||
    (config as any).authorId ||
    (config as any).user?.id ||
    (config as any).user?.userId
  );
};

// --- COMPOSANT DE RENDU (FRONT OFFICE) ---

const LiveClassRenderer: React.FC<PluginPreviewProps> = ({ config }) => {
  const classConfig = config as LiveClassConfig;
  const user = useSafeUser();

  const [hasTicket, setHasTicket] = useState(false);
  const [loading, setLoading] = useState(false);
  const [liveState, setLiveState] = useState<'upcoming' | 'live' | 'ended'>('upcoming');
  const [timeLeft, setTimeLeft] = useState('');
  const [selectedTariffId, setSelectedTariffId] = useState<string>('');
  const [promoInput, setPromoInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<LiveClassCoupon | null>(null);
  const [merchantKeys, setMerchantKeys] = useState<MerchantKeys>({});
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'success' | 'error' | 'cancelled'>('idle');
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalType, setLegalType] = useState<'cgv' | 'rgpd' | null>(null);

  const resolvedOwnerId = resolveOwnerId(classConfig);
  const ownerEmail = classConfig.ownerEmail;

  const stripePromise = useMemo(
    () => (merchantKeys.stripePublicKey ? loadStripe(merchantKeys.stripePublicKey) : null),
    [merchantKeys.stripePublicKey]
  );

  useEffect(() => {
    const checkTicket = async () => {
      if (!user || !classConfig.eventId) return;
      const isRegistered = classConfig.registrants?.some(r => String(r.id) === String(user.id));
      setHasTicket(isRegistered || false);
    };
    checkTicket();
  }, [user, classConfig.eventId, classConfig.registrants]);

  useEffect(() => {
    const firstTariff = classConfig.tariffs?.[0];
    if (firstTariff && !selectedTariffId) {
      setSelectedTariffId(firstTariff.id);
    }
  }, [classConfig.tariffs, selectedTariffId]);

  useEffect(() => {
    let cancelled = false;
    const fetchKeys = async () => {
      if (!resolvedOwnerId) {
        setMerchantKeys({});
        return;
      }
      try {
        const keys = (await fetchAPI(`/payment-keys/public/${resolvedOwnerId}`)) as MerchantKeys;
        if (cancelled) return;
        setMerchantKeys({
          stripePublicKey: keys?.stripePublicKey || null,
          paypalClientId: keys?.paypalClientId || null
        });
      } catch {
        if (!cancelled) setMerchantKeys({});
      }
    };

    fetchKeys();
    return () => { cancelled = true; };
  }, [resolvedOwnerId]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!classConfig.scheduledAt) return;
      const now = new Date().getTime();
      const start = new Date(classConfig.scheduledAt).getTime();
      const end = start + (classConfig.duration * 60 * 1000);

      if (now > end) {
        setLiveState('ended');
        setTimeLeft("Terminé"); 
      } else if (now >= start && now <= end) {
        setLiveState('live');
        setTimeLeft("EN DIRECT");
      } else {
        setLiveState('upcoming');
        const distance = start - now;
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        setTimeLeft(`${days}j ${hours}h ${minutes}m`);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [classConfig.scheduledAt, classConfig.duration]);

  const baseTariff = classConfig.tariffs?.find(t => t.id === selectedTariffId) || classConfig.tariffs?.[0];
  const basePrice = Math.max(0, Number(baseTariff?.price || 0));
  const currency = classConfig.currency || 'EUR';
  const discountValue = appliedCoupon
    ? appliedCoupon.type === 'percent'
      ? Math.round((basePrice * appliedCoupon.amount) / 100)
      : appliedCoupon.amount
    : 0;
  const finalPrice = Math.max(0, basePrice - discountValue);
  const lateRegistrationMinutes = Math.max(0, Number(classConfig.allowLateRegistrationMinutes || 0));
  const scheduledTime = classConfig.scheduledAt ? new Date(classConfig.scheduledAt).getTime() : NaN;
  const registrationClosed =
    Number.isFinite(scheduledTime) && Date.now() > scheduledTime + (lateRegistrationMinutes * 60 * 1000);

  const registerParticipant = async (method: 'Stripe' | 'PayPal' | 'Free', transactionId?: string) => {
    const ownerId = resolvedOwnerId !== undefined && resolvedOwnerId !== null ? Number(resolvedOwnerId) : undefined;
    if (!Number.isFinite(ownerId)) return;
    if (!user?.email) return;
    try {
      await fetchAPI('/live-class/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerId,
          ownerEmail,
          eventTitle: classConfig.title,
          eventId: classConfig.eventId,
          buyerName: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user?.name || 'Participant'),
          buyerEmail: user.email,
          buyerUserId: user?.id || user?.userId,
          amount: finalPrice,
          currency,
          paymentMethod: method.toLowerCase(),
          paymentStatus: 'PAID',
          transactionId,
          scheduledAt: classConfig.scheduledAt
        })
      });
    } catch (e) {
      console.error('Erreur notification paiement LiveClass:', e);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('stripe');
    if (!status) return;
    if (status === 'success') {
      setPaymentStatus('success');
      setPaymentMessage('Paiement confirmé. Votre accès est validé.');
      setHasTicket(true);
      const sessionId = params.get('session_id') || undefined;
      registerParticipant('Stripe', sessionId || undefined);
    } else if (status === 'cancel') {
      setPaymentStatus('cancelled');
      setPaymentMessage('Paiement annulé.');
    }
    params.delete('stripe');
    params.delete('session_id');
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', newUrl);
  }, []);

  const handleApplyCoupon = () => {
    const code = promoInput.trim().toLowerCase();
    if (!code) {
      setAppliedCoupon(null);
      return;
    }
    const coupon = (classConfig.coupons || []).find(c => c.active && c.code.toLowerCase() === code);
    if (!coupon) {
      alert('Code promo invalide.');
      setAppliedCoupon(null);
      return;
    }
    setAppliedCoupon(coupon);
  };

  const handleRegisterOrBuy = async () => {
    if (!user) {
      alert("Veuillez vous connecter pour vous inscrire.");
      return;
    }

    if (registrationClosed) {
      alert("Les inscriptions sont fermées pour cet événement.");
      return;
    }
    
    if (finalPrice === 0) {
        setLoading(true);
        setTimeout(() => {
            alert(`Inscription confirmée pour "${classConfig.title}" !`);
            setHasTicket(true); 
            setLoading(false);
            registerParticipant('Free');
        }, 1000);
        return;
    }

    setPaymentStatus('error');
    setPaymentMessage('Veuillez choisir un moyen de paiement ci-dessous.');
  };

  const openLegalModal = (type: 'cgv' | 'rgpd') => {
    setLegalType(type);
    setLegalModalOpen(true);
  };

  const closeLegalModal = () => {
    setLegalModalOpen(false);
    setLegalType(null);
  };

  const startStripeCheckout = async () => {
    if (!user) {
      alert("Veuillez vous connecter pour vous inscrire.");
      return;
    }
    if (registrationClosed) {
      setPaymentStatus('error');
      setPaymentMessage("Les inscriptions sont fermées pour cet événement.");
      return;
    }
    if (!resolvedOwnerId) {
      setPaymentStatus('error');
      setPaymentMessage('Propriétaire du plugin non défini.');
      return;
    }
    const stripe = await stripePromise;
    if (!stripe) {
      setPaymentStatus('error');
      setPaymentMessage('Stripe indisponible.');
      return;
    }

    setStripeBusy(true);
    setPaymentStatus('idle');
    setPaymentMessage(null);

    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const payload = {
        ownerId: Number(resolvedOwnerId),
        ownerEmail,
        eventTitle: classConfig.title,
        amount: finalPrice,
        currency,
        successUrl: baseUrl,
        cancelUrl: baseUrl,
        buyerEmail: user?.email,
        eventId: classConfig.eventId
      };
      const resp: any = await fetchAPI('/live-class/stripe-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!resp?.sessionId) {
        setPaymentStatus('error');
        setPaymentMessage(resp?.message || 'Impossible de créer la session Stripe.');
        setStripeBusy(false);
        return;
      }
      await stripe.redirectToCheckout({ sessionId: resp.sessionId });
    } catch (err: any) {
      setPaymentStatus('error');
      setPaymentMessage(err?.message || 'Erreur Stripe.');
      setStripeBusy(false);
    }
  };

  const handleJoin = async () => {
    try {
      if (!user) {
        alert("Utilisateur non identifié");
        return;
      }
      
      const resp = await getRtcToken({
        room: classConfig.eventId,
        identity: String(user.id),
        name: user.firstName || 'Participant'
      });

      const event = new CustomEvent('open-live-class', { 
        detail: { 
          url: resp.url,
          token: resp.token,
          room: classConfig.eventId,
          subject: classConfig.title
        } 
      });
      window.dispatchEvent(event);
      
    } catch (e) {
      console.error(e);
      alert("Impossible de rejoindre la conférence. Vérifiez votre connexion.");
    }
  };

  const renderBackground = () => {
    if (classConfig.coverMediaType === 'video' && classConfig.coverImage) {
      return (
        <video
          src={classConfig.coverImage}
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
        />
      );
    } else {
      const bgImage = classConfig.coverImage 
        ? `url(${classConfig.coverImage})` 
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      
      return (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `${bgImage} center/cover no-repeat`,
          zIndex: 0
        }} />
      );
    }
  };

  return (
    <div className="card shadow-sm border-0 overflow-hidden" style={{ maxWidth: '600px', margin: '20px auto', fontFamily: 'sans-serif' }}>
      <div style={{ height: '250px', position: 'relative', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
        {renderBackground()}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)', zIndex: 1 }}></div>
        <div style={{ position: 'relative', zIndex: 2, padding: '20px', color: 'white', width: '100%' }}>
            <span className="badge bg-danger mb-2">{liveState === 'live' ? '🔴 EN DIRECT' : (basePrice > 0 ? `${basePrice} ${currency}` : 'GRATUIT')}</span>
            <h3 className="m-0" style={{color: 'white', fontWeight: 'bold'}}>{classConfig.title}</h3>
            <small style={{fontSize: '0.9rem', opacity: 0.9}}>Avec {classConfig.instructorName}</small>
        </div>
      </div>

      <div className="card-body text-center p-4">
        <p className="text-muted mb-4">{classConfig.description}</p>

        {!hasTicket && registrationClosed && (
          <div className="alert alert-warning">
            Les inscriptions sont fermées pour cet événement.
          </div>
        )}

        {classConfig.tariffs && classConfig.tariffs.length > 0 && (
          <div className="mb-4 text-start">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Tarif</AutoTranslateText></label>
            <select
              className="form-select"
              value={selectedTariffId}
              onChange={(e) => {
                setSelectedTariffId(e.target.value);
                setAppliedCoupon(null);
              }}
            >
              {classConfig.tariffs.map((tariff) => (
                <option key={tariff.id} value={tariff.id}>
                  {tariff.name} — {tariff.price} {currency}
                </option>
              ))}
            </select>
            {baseTariff?.description && (
              <div className="text-muted small mt-1">{baseTariff.description}</div>
            )}
          </div>
        )}

        {classConfig.coupons && classConfig.coupons.length > 0 && (
          <div className="mb-4 text-start">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Code promo</AutoTranslateText></label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Votre code"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
              />
              <button className="btn btn-outline-primary" onClick={handleApplyCoupon}>
                Appliquer
              </button>
            </div>
            {appliedCoupon && (
              <div className="text-success small mt-1">
                Code appliqué : -{appliedCoupon.type === 'percent' ? `${appliedCoupon.amount}%` : `${appliedCoupon.amount} ${currency}`}
              </div>
            )}
          </div>
        )}

        {finalPrice > 0 && (
          <div className="mb-4 text-start">
            <div className="d-flex justify-content-between small">
              <span><AutoTranslateText sourceLang="fr">Prix</AutoTranslateText></span>
              <span>{basePrice} {currency}</span>
            </div>
            {discountValue > 0 && (
              <div className="d-flex justify-content-between small text-success">
                <span><AutoTranslateText sourceLang="fr">Réduction</AutoTranslateText></span>
                <span>-{discountValue} {currency}</span>
              </div>
            )}
            <div className="d-flex justify-content-between fw-bold mt-1">
              <span><AutoTranslateText sourceLang="fr">Total</AutoTranslateText></span>
              <span>{finalPrice} {currency}</span>
            </div>
          </div>
        )}

        {paymentStatus !== 'idle' && paymentMessage && (
          <div className={`alert ${paymentStatus === 'success' ? 'alert-success' : paymentStatus === 'cancelled' ? 'alert-warning' : 'alert-danger'}`}>
            {paymentMessage}
          </div>
        )}
        
        <div className="d-flex justify-content-center gap-4 mb-4 text-center">
          <div>
            <div className="fw-bold text-primary h4 mb-0">
              {classConfig.scheduledAt ? new Date(classConfig.scheduledAt).toLocaleDateString() : '--'}
            </div>
            <small className="text-muted"><AutoTranslateText sourceLang="fr">Date</AutoTranslateText></small>
          </div>
          <div>
            <div className="fw-bold text-primary h4 mb-0">
              {classConfig.scheduledAt ? new Date(classConfig.scheduledAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '--'}
            </div>
            <small className="text-muted"><AutoTranslateText sourceLang="fr">Heure</AutoTranslateText></small>
          </div>
          <div>
            <div className="fw-bold text-primary h4 mb-0">{classConfig.duration} min</div>
            <small className="text-muted"><AutoTranslateText sourceLang="fr">Durée</AutoTranslateText></small>
          </div>
        </div>

        <div className="d-grid gap-2">
          {!hasTicket ? (
            <button 
              className={`btn btn-lg ${finalPrice > 0 ? 'btn-primary' : 'btn-success'}`}
              onClick={handleRegisterOrBuy} 
              disabled={loading || registrationClosed}
            >
              {loading ? 'Traitement...' : (finalPrice > 0 ? `Acheter le billet (${finalPrice} ${currency})` : "S'inscrire gratuitement")}
            </button>
          ) : (
            <>
              <div className="alert alert-success py-2"><AutoTranslateText sourceLang="fr">✅ Vous êtes inscrit !</AutoTranslateText></div>
              {liveState === 'live' ? (
                <button className="btn btn-danger btn-lg pulse-animation" onClick={handleJoin}><AutoTranslateText sourceLang="fr">🎥 REJOINDRE LE DIRECT</AutoTranslateText></button>
              ) : liveState === 'ended' ? (
                <button className="btn btn-secondary" disabled><AutoTranslateText sourceLang="fr">L'événement est terminé</AutoTranslateText></button>
              ) : (
                <button className="btn btn-outline-primary" disabled>Le direct commence dans : {timeLeft}</button>
              )}
            </>
          )}
        </div>

        {!hasTicket && finalPrice > 0 && (
          <div className="mt-3 text-start">
            {!resolvedOwnerId && (
              <div className="alert alert-warning">
                Paiement indisponible : propriétaire du plugin non défini.
              </div>
            )}
            {resolvedOwnerId && !merchantKeys.stripePublicKey && !merchantKeys.paypalClientId && (
              <div className="alert alert-warning">
                Aucun moyen de paiement disponible pour cet événement.
              </div>
            )}

            {resolvedOwnerId && merchantKeys.stripePublicKey && stripePromise && (
              <div className="mb-3">
                <div style={{ fontWeight: 600, marginBottom: 8 }}><AutoTranslateText sourceLang="fr">Paiement par carte (Stripe Checkout)</AutoTranslateText></div>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={startStripeCheckout}
                  disabled={stripeBusy || isProcessing}
                >
                  {stripeBusy ? 'Redirection...' : 'Payer par carte'}
                </button>
              </div>
            )}

            {resolvedOwnerId && merchantKeys.paypalClientId && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}><AutoTranslateText sourceLang="fr">Paiement via PayPal</AutoTranslateText></div>
                <PayPalScriptProvider options={{ clientId: merchantKeys.paypalClientId, currency }}>
                  <PayPalCheckoutButtons
                    amount={finalPrice.toFixed(2)}
                    currency={currency}
                    onSuccess={async () => {
                      setPaymentStatus('success');
                      setPaymentMessage('Paiement confirmé. Votre accès est validé.');
                      setHasTicket(true);
                      await registerParticipant('PayPal');
                    }}
                    onError={(err) => {
                      setPaymentStatus('error');
                      setPaymentMessage(String(err || 'Erreur PayPal'));
                    }}
                    onCancel={() => {
                      setPaymentStatus('cancelled');
                      setPaymentMessage('Paiement annulé');
                    }}
                  />
                </PayPalScriptProvider>
              </div>
            )}
          </div>
        )}
      </div>

      {(classConfig.termsCgv || classConfig.termsRgpd) && (
        <div className="card-footer bg-white text-center">
          <div className="d-flex justify-content-center gap-3 flex-wrap small">
            {classConfig.termsCgv && (
              <button type="button" className="btn btn-link p-0" onClick={() => openLegalModal('cgv')}>
                CGU / CGV
              </button>
            )}
            {classConfig.termsRgpd && (
              <button type="button" className="btn btn-link p-0" onClick={() => openLegalModal('rgpd')}>
                RGPD
              </button>
            )}
          </div>
        </div>
      )}

      {legalModalOpen && legalType && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {legalType === 'cgv' ? 'CGU / CGV' : 'RGPD'}
                </h5>
                <button type="button" className="btn-close" onClick={closeLegalModal} aria-label="Close" />
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: legalType === 'cgv' ? (classConfig.termsCgv || '') : (classConfig.termsRgpd || '')
                  }}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeLegalModal}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- COMPOSANT DE CONFIGURATION (BACK OFFICE) ---

const LiveClassConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  const currentConfig = config as LiveClassConfig;
  const { openFileManager } = useFileManager();
  const { isLoading: keysLoading, hasStripeKey, hasPaypalKey } = usePaymentApiKeys();
  const { apiKey: tinymceApiKey, isLoading: tinymceLoading } = useTinyMceApiKey();
  const [activeTab, setActiveTab] = useState<'config' | 'tarifs' | 'payment' | 'alerts' | 'legal' | 'registrants' | 'paypal' | 'stripe'>('config');
  const [registrantsRemote, setRegistrantsRemote] = useState<Registrant[]>([]);
  const [registrantsLoading, setRegistrantsLoading] = useState(false);
  const [registrantsError, setRegistrantsError] = useState<string | null>(null);
  const alertDefaults: LiveClassAlertSettings = {
    enabled: false,
    channels: { notification: true, sms: false, whatsapp: false },
    advanceValue: 1,
    advanceUnit: 'hours',
    message: 'Votre live "{title}" commence dans {delay}. Rejoignez ici : {link}',
    sendAtStart: true,
    startMessage: 'Le live "{title}" est en direct. Cliquez ici : {link}'
  };
  const alertSettings = currentConfig.alertSettings || alertDefaults;

  useEffect(() => {
    if (!currentConfig.eventId) {
      onConfigChange({
        ...currentConfig,
        eventId: `class-${Date.now()}`,
        registrants: currentConfig.registrants || [],
        paymentMethods: currentConfig.paymentMethods || { stripe: false, paypal: false },
        tariffs: currentConfig.tariffs || [],
        coupons: currentConfig.coupons || [],
        alertSettings: currentConfig.alertSettings || alertDefaults
      });
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    if (currentConfig.ownerId || currentConfig.ownerEmail) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      const ownerEmail = userData?.email;
      if (ownerId || ownerEmail) {
        onConfigChange({
          ...currentConfig,
          ...(ownerId ? { ownerId: String(ownerId) } : {}),
          ...(ownerEmail ? { ownerEmail } : {})
        });
      }
    } catch {
      // ignore
    }
  }, [currentConfig, onConfigChange]);

  useEffect(() => {
    if (currentConfig.alertSettings) return;
    onConfigChange({
      ...currentConfig,
      alertSettings: alertDefaults
    });
  }, [currentConfig, onConfigChange, alertDefaults]);

  useEffect(() => {
    if (activeTab !== 'registrants') return;
    if (!currentConfig.eventId) return;
    let cancelled = false;
    const loadRegistrants = async () => {
      setRegistrantsLoading(true);
      setRegistrantsError(null);
      try {
        const resp: any = await fetchAPI(`/live-class/registrations?eventId=${encodeURIComponent(currentConfig.eventId)}`);
        if (!cancelled) {
          setRegistrantsRemote(Array.isArray(resp?.registrants) ? resp.registrants : []);
        }
      } catch (e: any) {
        if (!cancelled) setRegistrantsError(e?.message || 'Erreur chargement');
      } finally {
        if (!cancelled) setRegistrantsLoading(false);
      }
    };
    loadRegistrants();
    return () => { cancelled = true; };
  }, [activeTab, currentConfig.eventId]);

  const handleChange = (field: keyof LiveClassConfig, value: any) => {
    onConfigChange({ ...currentConfig, [field]: value });
  };

  const handleSelectMedia = (type: 'image' | 'video') => {
    openFileManager({
      type: type,
      onFileSelect: (file) => {
        if (file && file.url) {
          onConfigChange({
            ...currentConfig,
            coverImage: file.url,
            coverMediaType: type
          });
        }
      }
    });
  };

  const togglePaymentMethod = (method: 'stripe' | 'paypal', checked: boolean) => {
    onConfigChange({
        ...currentConfig,
        paymentMethods: {
            ...currentConfig.paymentMethods,
            [method]: checked
        }
    });
  };

  return (
    <div className="p-3">
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}><AutoTranslateText sourceLang="fr">⚙️ Configuration</AutoTranslateText></button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'tarifs' ? 'active' : ''}`} onClick={() => setActiveTab('tarifs')}><AutoTranslateText sourceLang="fr">🏷️ Tarifs</AutoTranslateText></button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`} onClick={() => setActiveTab('payment')}><AutoTranslateText sourceLang="fr">💳 Paiement</AutoTranslateText></button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'alerts' ? 'active' : ''}`} onClick={() => setActiveTab('alerts')}><AutoTranslateText sourceLang="fr">🔔 Alertes</AutoTranslateText></button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'legal' ? 'active' : ''}`} onClick={() => setActiveTab('legal')}><AutoTranslateText sourceLang="fr">📄 CGU/RGPD</AutoTranslateText></button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'registrants' ? 'active' : ''}`} onClick={() => setActiveTab('registrants')}><AutoTranslateText sourceLang="fr">👥 Inscrits</AutoTranslateText></button></li>
        {hasPaypalKey && <li className="nav-item"><button className={`nav-link ${activeTab === 'paypal' ? 'active' : ''}`} onClick={() => setActiveTab('paypal')}>🅿 PayPal</button></li>}
        {hasStripeKey && <li className="nav-item"><button className={`nav-link ${activeTab === 'stripe' ? 'active' : ''}`} onClick={() => setActiveTab('stripe')}>💜 Stripe</button></li>}
      </ul>

      {activeTab === 'config' && (
        <>
          <div className="mb-3">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Titre</AutoTranslateText></label>
            <input type="text" className="form-control" value={currentConfig.title} onChange={(e) => handleChange('title', e.target.value)} />
          </div>

          <div className="mb-3">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Description</AutoTranslateText></label>
            {tinymceLoading ? (
              <div><AutoTranslateText sourceLang="fr">Chargement...</AutoTranslateText></div>
            ) : !tinymceApiKey ? (
              <div className="alert alert-warning"><AutoTranslateText sourceLang="fr">Clé TinyMCE manquante</AutoTranslateText></div>
            ) : (
              <Editor
                apiKey={tinymceApiKey}
                value={currentConfig.description || ''}
                onEditorChange={(content) => handleChange('description', content)}
                init={withTabLink({
                  height: 220,
                  menubar: false,
                  plugins: ['link', 'lists', 'code', 'autolink'],
                  toolbar: 'undo redo | bold italic underline | bullist numlist | link | code',
                  content_style: `
                    body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                    img[style*="float: left"] { margin: 0 12px 12px 0; }
                    img[style*="float: right"] { margin: 0 0 12px 12px; }
                  `
                })}
              />
            )}
          </div>

          <div className="mb-3 p-3 border rounded bg-light">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Image ou Vidéo de fond</AutoTranslateText></label>
            <div className="d-flex gap-2 mb-2">
              <button className="btn btn-outline-primary btn-sm" onClick={() => handleSelectMedia('image')}><AutoTranslateText sourceLang="fr">🖼️ Image</AutoTranslateText></button>
              <button className="btn btn-outline-danger btn-sm" onClick={() => handleSelectMedia('video')}><AutoTranslateText sourceLang="fr">🎥 Vidéo</AutoTranslateText></button>
              {currentConfig.coverImage && (
                <button className="btn btn-outline-secondary btn-sm" onClick={() => handleChange('coverImage', '')}><AutoTranslateText sourceLang="fr">🗑️ Supprimer</AutoTranslateText></button>
              )}
            </div>
            {currentConfig.coverImage && (
              <div className="mt-2">
                {currentConfig.coverMediaType === 'video' ? (
                  <video src={currentConfig.coverImage} style={{width: '100%', maxHeight: '150px', objectFit: 'cover'}} autoPlay muted loop />
                ) : (
                  <img src={currentConfig.coverImage} alt="Cover" style={{width: '100%', maxHeight: '150px', objectFit: 'cover'}} />
                )}
              </div>
            )}
          </div>
          
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label"><AutoTranslateText sourceLang="fr">Date</AutoTranslateText></label>
              <input type="datetime-local" className="form-control" value={currentConfig.scheduledAt} onChange={(e) => handleChange('scheduledAt', e.target.value)} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label"><AutoTranslateText sourceLang="fr">Devise</AutoTranslateText></label>
              <input type="text" className="form-control" value={currentConfig.currency} onChange={(e) => handleChange('currency', e.target.value.toUpperCase())} />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label"><AutoTranslateText sourceLang="fr">Durée (min)</AutoTranslateText></label>
              <input type="number" className="form-control" value={currentConfig.duration} onChange={(e) => handleChange('duration', Number(e.target.value))} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label"><AutoTranslateText sourceLang="fr">Organisateur</AutoTranslateText></label>
              <input type="text" className="form-control" value={currentConfig.instructorName} onChange={(e) => handleChange('instructorName', e.target.value)} />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Autoriser l'inscription (minutes après le début)</AutoTranslateText></label>
            <input
              type="number"
              className="form-control"
              min={0}
              value={currentConfig.allowLateRegistrationMinutes ?? 0}
              onChange={(e) => handleChange('allowLateRegistrationMinutes', Math.max(0, Number(e.target.value)))}
            />
            <div className="text-muted small mt-1">
              0 = inscriptions fermées dès le début.
            </div>
          </div>

        </>
      )}

      {activeTab === 'tarifs' && (
        <div className="animate-fade-in">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="m-0"><AutoTranslateText sourceLang="fr">Tarifs</AutoTranslateText></h6>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                const newTariff: LiveClassTariff = {
                  id: `tariff_${Date.now()}`,
                  name: 'Standard',
                  price: 0
                };
                onConfigChange({
                  ...currentConfig,
                  tariffs: [...(currentConfig.tariffs || []), newTariff]
                });
              }}
            >
              ➕ Ajouter
            </button>
          </div>

          {(currentConfig.tariffs || []).length === 0 && (
            <div className="text-muted small"><AutoTranslateText sourceLang="fr">Aucun tarif défini. Ajoutez un tarif pour activer les paiements.</AutoTranslateText></div>
          )}

          <div className="d-flex flex-column gap-2">
            {(currentConfig.tariffs || []).map((tariff, idx) => (
              <div key={tariff.id} className="card border">
                <div className="card-body p-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={tariff.name}
                      onChange={(e) => {
                        const tariffs = [...(currentConfig.tariffs || [])];
                        tariffs[idx] = { ...tariff, name: e.target.value };
                        onConfigChange({ ...currentConfig, tariffs });
                      }}
                    />
                    <button
                      className="btn btn-link text-danger btn-sm"
                      onClick={() => {
                        const tariffs = [...(currentConfig.tariffs || [])];
                        tariffs.splice(idx, 1);
                        onConfigChange({ ...currentConfig, tariffs });
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={tariff.price}
                        onChange={(e) => {
                          const tariffs = [...(currentConfig.tariffs || [])];
                          tariffs[idx] = { ...tariff, price: Number(e.target.value) };
                          onConfigChange({ ...currentConfig, tariffs });
                        }}
                        placeholder="Prix"
                      />
                    </div>
                    <div className="col-6">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={tariff.description || ''}
                        onChange={(e) => {
                          const tariffs = [...(currentConfig.tariffs || [])];
                          tariffs[idx] = { ...tariff, description: e.target.value };
                          onConfigChange({ ...currentConfig, tariffs });
                        }}
                        placeholder="Description"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <hr className="my-3" />

          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="m-0"><AutoTranslateText sourceLang="fr">Codes promo</AutoTranslateText></h6>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                const coupons = [...(currentConfig.coupons || [])];
                coupons.push({ code: '', type: 'percent', amount: 10, active: true });
                onConfigChange({ ...currentConfig, coupons });
              }}
            >
              ➕ Ajouter
            </button>
          </div>

          {(currentConfig.coupons || []).length === 0 && (
            <div className="text-muted small"><AutoTranslateText sourceLang="fr">Aucun code promo défini.</AutoTranslateText></div>
          )}

          <div className="d-flex flex-column gap-2">
            {(currentConfig.coupons || []).map((coupon, idx) => (
              <div key={`${coupon.code}-${idx}`} className="card border">
                <div className="card-body p-2">
                  <div className="row g-2 align-items-center">
                    <div className="col-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={coupon.code}
                        onChange={(e) => {
                          const coupons = [...(currentConfig.coupons || [])];
                          coupons[idx] = { ...coupon, code: e.target.value.toUpperCase() };
                          onConfigChange({ ...currentConfig, coupons });
                        }}
                        placeholder="CODE"
                      />
                    </div>
                    <div className="col-3">
                      <select
                        className="form-select form-select-sm"
                        value={coupon.type}
                        onChange={(e) => {
                          const coupons = [...(currentConfig.coupons || [])];
                          coupons[idx] = { ...coupon, type: e.target.value as LiveClassCoupon['type'] };
                          onConfigChange({ ...currentConfig, coupons });
                        }}
                      >
                        <option value="percent">%</option>
                        <option value="fixed"><AutoTranslateText sourceLang="fr">Montant</AutoTranslateText></option>
                      </select>
                    </div>
                    <div className="col-3">
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={coupon.amount}
                        onChange={(e) => {
                          const coupons = [...(currentConfig.coupons || [])];
                          coupons[idx] = { ...coupon, amount: Number(e.target.value) };
                          onConfigChange({ ...currentConfig, coupons });
                        }}
                      />
                    </div>
                    <div className="col-2 d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={coupon.active}
                        onChange={(e) => {
                          const coupons = [...(currentConfig.coupons || [])];
                          coupons[idx] = { ...coupon, active: e.target.checked };
                          onConfigChange({ ...currentConfig, coupons });
                        }}
                      />
                      <button
                        className="btn btn-link text-danger btn-sm p-0"
                        onClick={() => {
                          const coupons = [...(currentConfig.coupons || [])];
                          coupons.splice(idx, 1);
                          onConfigChange({ ...currentConfig, coupons });
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="animate-fade-in">
          <div className="alert alert-info"><AutoTranslateText sourceLang="fr">Configuration des méthodes de paiement</AutoTranslateText></div>
          {keysLoading ? (
            <div className="spinner-border spinner-border-sm text-primary"></div>
          ) : (
            <>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="live-pay-stripe"
                  checked={currentConfig.paymentMethods?.stripe || false}
                  onChange={(e) => togglePaymentMethod('stripe', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="live-pay-stripe"><AutoTranslateText sourceLang="fr">Stripe</AutoTranslateText></label>
                {!hasStripeKey && <div className="text-muted small"><AutoTranslateText sourceLang="fr">Clé Stripe manquante.</AutoTranslateText></div>}
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="live-pay-paypal"
                  checked={currentConfig.paymentMethods?.paypal || false}
                  onChange={(e) => togglePaymentMethod('paypal', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="live-pay-paypal"><AutoTranslateText sourceLang="fr">PayPal</AutoTranslateText></label>
                {!hasPaypalKey && <div className="text-muted small"><AutoTranslateText sourceLang="fr">Clé PayPal manquante.</AutoTranslateText></div>}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="animate-fade-in">
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="live-alerts-enabled"
              checked={alertSettings.enabled}
              onChange={(e) => handleChange('alertSettings', { ...alertSettings, enabled: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="live-alerts-enabled">
              Activer les alertes pour les inscrits
            </label>
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Type d’alerte</AutoTranslateText></label>
            <div className="d-flex flex-wrap gap-3">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={alertSettings.channels.notification}
                  onChange={(e) =>
                    handleChange('alertSettings', {
                      ...alertSettings,
                      channels: { ...alertSettings.channels, notification: e.target.checked }
                    })
                  }
                />
                <span className="form-check-label"><AutoTranslateText sourceLang="fr">Notification</AutoTranslateText></span>
              </label>
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={alertSettings.channels.sms}
                  onChange={(e) =>
                    handleChange('alertSettings', {
                      ...alertSettings,
                      channels: { ...alertSettings.channels, sms: e.target.checked }
                    })
                  }
                />
                <span className="form-check-label"><AutoTranslateText sourceLang="fr">SMS</AutoTranslateText></span>
              </label>
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={alertSettings.channels.whatsapp}
                  onChange={(e) =>
                    handleChange('alertSettings', {
                      ...alertSettings,
                      channels: { ...alertSettings.channels, whatsapp: e.target.checked }
                    })
                  }
                />
                <span className="form-check-label"><AutoTranslateText sourceLang="fr">WhatsApp</AutoTranslateText></span>
              </label>
            </div>
            <div className="text-muted small mt-1">
              SMS/WhatsApp nécessitent un numéro de téléphone pour chaque inscrit.
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Quand envoyer le rappel</AutoTranslateText></label>
            <div className="row g-2 align-items-center">
              <div className="col-4 col-md-3">
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  value={alertSettings.advanceValue}
                  onChange={(e) =>
                    handleChange('alertSettings', {
                      ...alertSettings,
                      advanceValue: Math.max(1, Number(e.target.value))
                    })
                  }
                />
              </div>
              <div className="col-8 col-md-4">
                <select
                  className="form-select"
                  value={alertSettings.advanceUnit}
                  onChange={(e) =>
                    handleChange('alertSettings', {
                      ...alertSettings,
                      advanceUnit: e.target.value as LiveClassAlertSettings['advanceUnit']
                    })
                  }
                >
                  <option value="minutes"><AutoTranslateText sourceLang="fr">Minutes avant</AutoTranslateText></option>
                  <option value="hours"><AutoTranslateText sourceLang="fr">Heures avant</AutoTranslateText></option>
                  <option value="days"><AutoTranslateText sourceLang="fr">Jours avant</AutoTranslateText></option>
                </select>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Message de rappel</AutoTranslateText></label>
            <textarea
              className="form-control"
              rows={3}
              value={alertSettings.message}
              onChange={(e) => handleChange('alertSettings', { ...alertSettings, message: e.target.value })}
            />
            <div className="text-muted small mt-1">
              Variables disponibles : {"{title}"}, {"{date}"}, {"{time}"}, {"{delay}"}, {"{link}"}
            </div>
          </div>

          <div className="border rounded p-3 bg-light">
            <div className="form-check form-switch mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="live-alerts-start"
                checked={alertSettings.sendAtStart}
                onChange={(e) => handleChange('alertSettings', { ...alertSettings, sendAtStart: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="live-alerts-start">
                Envoyer une notification au démarrage du live
              </label>
            </div>
            <textarea
              className="form-control"
              rows={3}
              value={alertSettings.startMessage}
              onChange={(e) => handleChange('alertSettings', { ...alertSettings, startMessage: e.target.value })}
            />
            <div className="text-muted small mt-1">
              Ce message doit contenir le lien d’accès. Exemple : {"{link}"}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'legal' && (
        <div className="animate-fade-in">
          <div className="mb-4">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">CGU / CGV</AutoTranslateText></label>
            {tinymceLoading ? (
              <div><AutoTranslateText sourceLang="fr">Chargement...</AutoTranslateText></div>
            ) : !tinymceApiKey ? (
              <div className="alert alert-warning"><AutoTranslateText sourceLang="fr">Clé TinyMCE manquante</AutoTranslateText></div>
            ) : (
              <Editor
                apiKey={tinymceApiKey}
                value={currentConfig.termsCgv || ''}
                onEditorChange={(content) => handleChange('termsCgv', content)}
                init={withTabLink({
                  height: 260,
                  menubar: false,
                  plugins: ['link', 'lists', 'code', 'autolink'],
                  toolbar: 'undo redo | bold italic underline | bullist numlist | link | code',
                  content_style: `
                    body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                    img[style*="float: left"] { margin: 0 12px 12px 0; }
                    img[style*="float: right"] { margin: 0 0 12px 12px; }
                  `
                })}
              />
            )}
          </div>

          <div className="mb-2">
            <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">RGPD</AutoTranslateText></label>
            {tinymceLoading ? (
              <div><AutoTranslateText sourceLang="fr">Chargement...</AutoTranslateText></div>
            ) : !tinymceApiKey ? (
              <div className="alert alert-warning"><AutoTranslateText sourceLang="fr">Clé TinyMCE manquante</AutoTranslateText></div>
            ) : (
              <Editor
                apiKey={tinymceApiKey}
                value={currentConfig.termsRgpd || ''}
                onEditorChange={(content) => handleChange('termsRgpd', content)}
                init={withTabLink({
                  height: 260,
                  menubar: false,
                  plugins: ['link', 'lists', 'code', 'autolink'],
                  toolbar: 'undo redo | bold italic underline | bullist numlist | link | code',
                  content_style: `
                    body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                    img[style*="float: left"] { margin: 0 12px 12px 0; }
                    img[style*="float: right"] { margin: 0 0 12px 12px; }
                  `
                })}
              />
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'registrants' && (
        <div className="bg-white p-3 border rounded">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
              <h6 className="m-0"><AutoTranslateText sourceLang="fr">Liste des participants</AutoTranslateText></h6>
              <span className="badge bg-primary">{registrantsRemote.length}</span>
            </div>
            <button 
                className="btn btn-sm btn-outline-danger"
                onClick={async () => {
                    if (!currentConfig.eventId) return;
                    if(confirm("Voulez-vous vraiment vider la liste des inscrits ?")) {
                        try {
                          await fetchAPI(`/live-class/registrations?eventId=${encodeURIComponent(currentConfig.eventId)}`, { method: 'DELETE' });
                          setRegistrantsRemote([]);
                        } catch (e: any) {
                          setRegistrantsError(e?.message || 'Erreur suppression');
                        }
                    }
                }}
            >
                🗑️ Tout supprimer
            </button>
          </div>
          {registrantsLoading && (
            <div className="text-center text-muted py-4"><p><AutoTranslateText sourceLang="fr">Chargement...</AutoTranslateText></p></div>
          )}
          {registrantsError && (
            <div className="alert alert-warning">{registrantsError}</div>
          )}
          {!registrantsLoading && !registrantsError && (registrantsRemote.length === 0) ? (
            <div className="text-center text-muted py-4"><p><AutoTranslateText sourceLang="fr">Aucun inscrit pour le moment.</AutoTranslateText></p></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead className="table-light"><tr><th><AutoTranslateText sourceLang="fr">Nom</AutoTranslateText></th><th><AutoTranslateText sourceLang="fr">Email</AutoTranslateText></th><th><AutoTranslateText sourceLang="fr">Date</AutoTranslateText></th><th><AutoTranslateText sourceLang="fr">Statut</AutoTranslateText></th><th><AutoTranslateText sourceLang="fr">Montant</AutoTranslateText></th><th><AutoTranslateText sourceLang="fr">Méthode</AutoTranslateText></th></tr></thead>
                <tbody>
                  {registrantsRemote.map((reg, idx) => (
                    <tr key={idx}>
                      <td>{reg.name}</td>
                      <td>{reg.email}</td>
                      <td>{new Date(reg.registeredAt).toLocaleDateString()}</td>
                      <td><span className={`badge ${reg.status === 'confirmed' ? 'bg-success' : 'bg-warning'}`}>{reg.status === 'confirmed' ? 'Confirmé' : 'En attente'}</span></td>
                      <td>{(reg as any).amountCents ? `${(((reg as any).amountCents || 0) / 100).toFixed(2)} ${(reg as any).currency || 'EUR'}` : '—'}</td>
                      <td>{(reg as any).paymentMethod || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'paypal' && (
        <PayPalOrdersPanel pluginType="liveclass" shopId={currentConfig.id} />
      )}
      {activeTab === 'stripe' && (
        <StripeOrdersPanel pluginType="liveclass" shopId={currentConfig.id} />
      )}
    </div>
  );
};

// --- DÉFINITION DU PLUGIN ---

const LiveClassPluginModular: PluginInterface = {
  metadata: {
    id: 'live-class-modular',
    name: "Vidéo Conférence",
    description: "Vendez ou offrez des accès à des visioconférences.",
    version: '1.1.0',
    author: 'FlexiHub',
    category: 'business',
    icon: '🎓',
    tags: ['live', 'cours', 'webinar']
  },
  
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  // ✅ DOCUMENTATION POUR L'IA (Nouveau standard)
  documentationForAI: {
    description: "Composant pour vendre ou diffuser un cours en direct ou un webinaire.",
    usage: "Utilise ce plugin quand l'utilisateur veut organiser un événement live payant ou gratuit.",
    configSchema: {
      title: "Titre du live",
      tariffs: "Liste de tarifs (VIP, Adulte, Enfant)",
      scheduledAt: "Date ISO string",
      duration: "Durée en minutes",
      allowLateRegistrationMinutes: "Délai d'inscription après le début (minutes)",
      alertSettings: "Paramètres d'alertes (canal, délai, messages)"
    },
    exampleConfig: {
      title: "Masterclass Yoga",
      tariffs: [{ id: "vip", name: "VIP", price: 49 }],
      scheduledAt: "2024-12-01T18:00:00",
      duration: 60,
      allowLateRegistrationMinutes: 10,
      alertSettings: {
        enabled: true,
        channels: { notification: true, sms: false, whatsapp: false },
        advanceValue: 2,
        advanceUnit: "hours",
        message: "Votre live \"{title}\" commence dans {delay}. Accès : {link}",
        sendAtStart: true,
        startMessage: "Le live \"{title}\" est en direct. Cliquez ici : {link}"
      }
    }
  },

  ConfigComponent: LiveClassConfigComponent,
  PreviewComponent: LiveClassRenderer,
  
  // ✅ CORRECTION MAJEURE : GÉNÉRATION D'ID UNIQUE + PERSISTANCE
  generateHTML: (config: LiveClassConfig) => {
      // 1. Génération ID unique standard
      const instanceId = config.id || `live-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      // 2. On s'assure d'avoir l'ID dans la config
      const configWithId = { 
          ...config, 
          id: instanceId,
          // On garde eventId car c'est le nom de la room LiveKit
          eventId: config.eventId || `room-${instanceId}`
      };

      return generateUniformPlaceholder({
        pluginId: instanceId, // ✅ Cible unique pour l'édition
        pluginType: 'live-class-modular',
        title: config.title || 'Live Class',
        description: `${(config.tariffs?.[0]?.price ?? 0) > 0 ? config.tariffs?.[0]?.price + '€' : 'Gratuit'} • ${config.duration} min`,
        icon: '🎓',
        category: 'business',
        config: configWithId // ✅ On passe la config enrichie
      });
  },
  
  defaultConfig: {
    eventId: '',
    title: 'Ma Conférence',
    description: 'Rejoignez-nous en direct.',
    currency: 'EUR',
    scheduledAt: new Date().toISOString().slice(0, 16),
    duration: 60,
    allowLateRegistrationMinutes: 0,
    instructorName: 'Votre Nom',
    registrants: [],
    tariffs: [],
    coupons: [],
    alertSettings: {
      enabled: false,
      channels: { notification: true, sms: false, whatsapp: false },
      advanceValue: 1,
      advanceUnit: 'hours',
      message: 'Votre live "{title}" commence dans {delay}. Rejoignez ici : {link}',
      sendAtStart: true,
      startMessage: 'Le live "{title}" est en direct. Cliquez ici : {link}'
    },
    paymentMethods: {
      stripe: false,
      paypal: false
    }
  }
};

export default LiveClassPluginModular;
